import React, { useState, useMemo } from 'react';
import ImprovedSecondarySideBar from '../ImprovedSecondarySideBar';
import MenuButton from '../MenuButton';
import { isTabletView } from '../ReactiveRender';
import Loader from '../Loader';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { acceptShare, rejectShare, getMarkingMethodology, getCommentBank, getAssignmentById, getSuccessCriteria, getShortAnswer, neverShowSharedResourcePopup } from '../../service';
import { useShares, useProfile } from '../state/hooks';
import { useClassSubjects } from '../../hooks/useSubjects';
import { createShareListManager } from '../../utils/shareMutations';
import ActionPopup from '../../components2/ActionPopup';
import DropdownMenu from '../DropdownMenu';
import CommentBankDialog from '../Shared/Dialogs/commentBank';
import PreviewDialog from '../Shared/Dialogs/preview/previewCard';
import TaskPreviewDialog from '../Shared/Dialogs/taskPreview';
import SuccessCriteriaDialog from '../Shared/Dialogs/successCriteria';
import ShortAnswerDialog from '../Shared/Dialogs/shortAnswer';
import {
  MainContainer,
  ContentContainer,
  HeaderContainer,
  TitleContainer,
  Title,
  FilterSortContainer,
  FilterContainer,
  FilterLabel,
  FilterDropdownWrap,
  SortContainer,
  SortLabel,
  SortButton,
  TableContainer,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  HeaderCellContent,
  VerticalDivider,
  TableBody,
  TableRow,
  TemplateName,
  TemplateType,
  SharedBy,
  Subject,
  SharedOn,
  ActionButtons,
} from './style';
import {
  PreviewButton,
  AcceptButton,
  RejectButton,
} from '../NotificationsMenu/NotificationsBar/style';
import previewIcon from '../../static/img/preview.svg';
import acceptIcon from '../../static/img/accept-icon.svg';
import rejectIcon from '../../static/icons/red-cross.svg';
import ShareFilterIcon from '../../static/icons/SharedFilter.svg';
import FilterSquare from '../../static/img/filter-square.svg';
import SortSquare from '../../static/img/sort-square.svg';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString(undefined, { month: 'long' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatResourceType = (resourceType) => {
  if (!resourceType) return '';
  if (resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS') {
    return 'Comment Bank';
  }
  if (resourceType === 'TASK') {
    return 'Task';
  }
  if (resourceType === 'SUCCESS_CRITERIA') {
    return 'Success Criterias';
  }
  if (resourceType === 'SHORT_ANSWERS') {
    return 'Short Answer';
  }
  return 'Marking Template';
};

const SharedResourcesList = ({
  resourceCategory = null,
  allowedResourceTypes = null,
  resourceNameColumnLabel = 'Resource name',
  showTypeColumn: showTypeColumnProp = null,
  onCloseNotification = null,
  successConfig = {
    message: (resource) => {
      const resourceType = resource?.resourceType || resource?.itemType || resource?.documentType;
      if (resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS') {
        return `The shared comment bank has been added to your Comment Banks.`;
      }
      if (resourceType === 'SUCCESS_CRITERIA') {
        return `The shared success criteria has been added to your Success Criterias.`;
      }
      if (resourceType === 'SHORT_ANSWERS') {
        return `The shared short answer has been added to your Short Answers.`;
      }
      if (resourceType === 'TASK') {
        return `The shared task has been added to your Tasks.`;
      }
      return `The shared marking template has been added to your Rubrics.`;
    },
    redirectPath: (resource) => {
      const resourceType = resource?.resourceType || resource?.itemType || resource?.documentType;
      if (resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS') {
        return '/#/tools/commentbanks';
      }
      if (resourceType === 'SUCCESS_CRITERIA') {
        return '/#/tools/successcriterias';
      }
      if (resourceType === 'SHORT_ANSWERS') {
        return '/#/tools/shortanswers';
      }
      if (resourceType === 'TASK') {
        return '/#/tasks';
      }
      return '/#/tools/markingcriterias';
    },
    buttonLabel: (resource) => {
      const resourceType = resource?.resourceType || resource?.itemType || resource?.documentType;
      if (resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS') {
        return 'Go to Comment Banks';
      }
      if (resourceType === 'SUCCESS_CRITERIA') {
        return 'Go to Success Criterias';
      }
      if (resourceType === 'SHORT_ANSWERS') {
        return 'Go to Short Answers';
      }
      if (resourceType === 'TASK') {
        return 'Go to Tasks';
      }
      return 'Go to Rubrics';
    },
  },
  rejectConfig = {
    title: 'Reject shared resource?',
    message: 'Once rejected the shared resource will be lost forever. Are you sure you want to continue?',
  },
  onAcceptSuccess = null,
  onPreview = null,
}) => {
  const [isShowMenu, setShowMenu] = useState(false);
  const [sortOrder, setSortOrder] = useState('newestFirst');
  const [selectedSharedBy, setSelectedSharedBy] = useState('Shared by');
  const [selectedSubject, setSelectedSubject] = useState('All Subjects');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourcePreviewData, setResourcePreviewData] = useState(null);
  const [previewedSharedResource, setPreviewedSharedResource] = useState(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isLoadingResourcePreview, setIsLoadingResourcePreview] = useState(false);
  const tabletView = isTabletView();
  const queryClient = useQueryClient();
  const { data: profile, isLoadingdata: isLoadingProfile, resetData: resetProfile } = useProfile();
  const { subjects: classSubjects } = useClassSubjects();
  const sharesParams = useMemo(() => resourceCategory, [resourceCategory]);
  const subjectCodeToTitle = useMemo(() => {
    const map = {};
    (classSubjects || []).forEach((s) => { if (s.code) map[s.code] = s.title; });
    return map;
  }, [classSubjects]);
  const { data: sharedResourcesData, isLoadingdata: isLoadingSharedResources } = useShares(sharesParams);

  const shouldShowPopup = (resourceType) => {
    if (!profile) {
      return true;
    }
    return profile.showSharedResourcesPopup !== false;
  };

  const handleCheckboxChange = async (checked) => {
    try {
      if (checked) {
        await neverShowSharedResourcePopup();
        resetProfile();
      }
    } catch (error) {
      console.error('Failed to update popup preference:', error);
    }
  };

  const shareListManager = createShareListManager(queryClient, sharesParams);

  const acceptShareRequest = useMutation({
    mutationFn: async ({ id: shareIdToAccept }) => {
      return await acceptShare(shareIdToAccept);
    },
    onMutate: shareListManager.removeShareFromList,
    onError: (error, actionData, savedState) => {
      shareListManager.restoreShareList(error, actionData, savedState, 'Failed to accept shared document:');
      queryClient.refetchQueries({ queryKey: ['notifications'] });
    },
    onSuccess: async (responseData) => {
      const shareListKey = shareListManager.getShareListKey();
      queryClient.refetchQueries({ queryKey: ['shares'] });
      queryClient.refetchQueries({ queryKey: shareListKey });
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      const resourceType = selectedResource?.resourceType;
      if (resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS') {
        queryClient.invalidateQueries({ queryKey: ['feedbackBanks'] });
        queryClient.invalidateQueries({ queryKey: ['commentBanksWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'commentBanks'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'] });

      } else if (resourceType === 'MARKING_TEMPLATE') {
        queryClient.invalidateQueries({ queryKey: ['markingCriterias'] });
        queryClient.invalidateQueries({ queryKey: ['markingCriteriasWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'markingCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'markingCriteria'] });
      } else if (resourceType === 'TASK') {
        queryClient.invalidateQueries({ queryKey: ['completedTasks'] });
        queryClient.invalidateQueries({ queryKey: ['assignments'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      } else if (resourceType === 'SHORT_ANSWERS') {
        queryClient.invalidateQueries({ queryKey: ['shortAnswers'] });
        queryClient.invalidateQueries({ queryKey: ['shortAnswersWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'shortAnswers'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'shortAnswers'] });
      } else if (resourceType === 'SUCCESS_CRITERIA') {
        queryClient.invalidateQueries({ queryKey: ['successCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['successCriteriaWithPreferences'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
        queryClient.invalidateQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
        queryClient.refetchQueries({ queryKey: ['successCriteria'] });
        queryClient.refetchQueries({ queryKey: ['successCriteriaWithPreferences'] });
        queryClient.refetchQueries({ queryKey: ['feedbackTool', 'items', 'successCriteria'] });
        queryClient.refetchQueries({ queryKey: ['feedbackTool', 'itemsCount', 'successCriteria'] });
      }
      if (onAcceptSuccess) {
        onAcceptSuccess(responseData, selectedResource);
      } else {
        const resourceType = selectedResource?.resourceType;
        if (shouldShowPopup(resourceType)) {
          setShowSuccessPopup(true);
        } else {
          if (onCloseNotification) {
            onCloseNotification();
          }
          if (window.closeNotificationBar) {
            window.closeNotificationBar();
          }
        }
      }
    },
  });

  const rejectShareRequest = useMutation({
    mutationFn: async ({ id: shareIdToReject }) => {
      return await rejectShare(shareIdToReject);
    },
    onMutate: shareListManager.removeShareFromList,
    onError: (error, actionData, savedState) => {
      shareListManager.restoreShareList(error, actionData, savedState, 'Failed to reject shared resource:');
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      setShowRejectPopup(false);
      setSelectedResource(null);
    },
    onSuccess: () => {
      const shareListKey = shareListManager.getShareListKey();
      queryClient.refetchQueries({ queryKey: ['shares'] });
      queryClient.refetchQueries({ queryKey: shareListKey });
      queryClient.refetchQueries({ queryKey: ['notifications'] });
      setShowRejectPopup(false);
      setSelectedResource(null);
    },
  });

  if (isLoadingProfile || isLoadingSharedResources) {
    return (
      <MainContainer>
        <ImprovedSecondarySideBar isShowMenu={isShowMenu} setShowMenu={setShowMenu} />
        <ContentContainer>
          <Loader />
        </ContentContainer>
      </MainContainer>
    );
  }

  const allSharedResources = Array.isArray(sharedResourcesData) ? sharedResourcesData : (sharedResourcesData?.data || sharedResourcesData?.items || []);

  const handleAccept = (sharedResource) => {
    const sharedResourceId = sharedResource.id || sharedResource.itemId;
    if (!sharedResourceId) {
      console.error('Cannot accept resource without ID:', sharedResource);
      return;
    }
    setSelectedResource(sharedResource);
    acceptShareRequest.mutate({ id: sharedResourceId });
  };

  const handleRejectClick = (sharedResource) => {
    if (!sharedResource?.id) {
      console.error('Cannot reject resource without ID:', sharedResource);
      return;
    }
    setSelectedResource(sharedResource);
    setShowRejectPopup(true);
  };

  const handleConfirmReject = () => {
    const shareIdToReject = selectedResource?.id || selectedResource?.itemId;
    if (!shareIdToReject) {
      console.error('Cannot reject resource without ID:', selectedResource);
      return;
    }
    rejectShareRequest.mutate({ id: shareIdToReject });
  };

  const handlePreviewClick = async (sharedResource) => {
    if (onPreview) {
      onPreview(sharedResource);
      return;
    }

    const itemId = sharedResource.itemId;
    const resourceType = sharedResource.resourceType;
    const sharedResourceId = sharedResource.id;
    
    if (!itemId) {
      console.error('SharedResources - No itemId found for preview:', sharedResource);
      return;
    }
    
    setPreviewedSharedResource(sharedResource);
    setIsLoadingResourcePreview(true);
    try {
      const isCommentBank = resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS';
      const isTask = resourceType === 'TASK';
      const isSuccessCriteria = resourceType === 'SUCCESS_CRITERIA';
      const isShortAnswer = resourceType === 'SHORT_ANSWERS';

      if (isCommentBank) {
        const data = await getCommentBank(itemId);
        setResourcePreviewData(data || { smartComments: [] });
      } else if (isTask) {
        const data = await getAssignmentById(itemId);
        setResourcePreviewData(data || { title: 'Untitled Task' });
      } else if (isSuccessCriteria) {
        const response = await getSuccessCriteria(itemId);
        const rawData = Array.isArray(response) && response.length > 0 ? response[0] : response;
        const data = rawData || { title: 'Untitled Success Criterias', criterias: [] };
        if (!data.criterias) {
          data.criterias = [];
        }
        setResourcePreviewData(data);
      } else if (isShortAnswer) {
        const response = await getShortAnswer(itemId);
        const rawData = Array.isArray(response) && response.length > 0 ? response[0] : response;
        const data = rawData || { title: 'Untitled Short Answer', highestMarks: 0, criterias: [] };
        if (!data.criterias) {
          data.criterias = [];
        }
        setResourcePreviewData(data);
      } else {
        let data = await getMarkingMethodology(itemId);
        if (Array.isArray(data) && data.length > 0) {
          data = data[0];
        }
        setResourcePreviewData(data || { type: 'RUBRICS', criterias: [] });
      }
      setIsPreviewDialogOpen(true);
    } catch (error) {
      console.error('SharedResources - Preview error:', error);
      const isCommentBank = resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS';
      const isTask = resourceType === 'TASK';
      const isSuccessCriteria = resourceType === 'SUCCESS_CRITERIA';
      const isShortAnswer = resourceType === 'SHORT_ANSWERS';
      const emptyData = isCommentBank
        ? { smartComments: [] }
        : isTask
        ? { title: 'Untitled Task' }
        : isSuccessCriteria
        ? { title: 'Untitled Success Criterias', criterias: [] }
        : isShortAnswer
        ? { title: 'Untitled Short Answer', highestMarks: 0, criterias: [] }
        : { type: 'RUBRICS', criterias: [] };
      setResourcePreviewData(emptyData);
      setIsPreviewDialogOpen(true);
    } finally {
      setIsLoadingResourcePreview(false);
    }
  };
  
  const mappedResources = allSharedResources
    .map((share) => {
      const resource = share.resource || share.sharedDocument;
      const item = resource?.item || {};

      const shareId = share.id || resource?.id;
      const itemId = resource?.itemId || item?.id;
      const itemTitle = share.title || item?.title || resource?.title;
      const sharedOn = share.sharedOn || resource?.sharedOn || share.sharedDocument?.sharedOn || resource?.sharedDocument?.sharedOn || share.resource?.sharedOn;
      const sharedBy = share.senderName || share.sharedBy;
      const resourceType = resource?.itemType || share.itemType || share.documentType;

      const rawSubjects = share.subjects || [];
      const subjects = Array.isArray(rawSubjects) && rawSubjects.length > 0
        ? rawSubjects.map((s) => ({
            code: s.subjectCode ?? s.code ?? '',
            name: s.subjectName ?? s.name ?? s.title ?? '',
          }))
        : [];

      const formattedDate = formatDate(sharedOn);

      return {
        id: shareId,
        itemId: itemId,
        itemTitle: itemTitle,
        sharedOn: formattedDate,
        sharedOnDate: sharedOn ? new Date(sharedOn) : new Date(0),
        sharedBy: sharedBy,
        resourceType: resourceType,
        subjects,
      };
    });
  
  const validResources = mappedResources.filter((resource) => !!(resource.id || resource.itemId));
  
  const filteredByResourceType = allowedResourceTypes && Array.isArray(allowedResourceTypes) && allowedResourceTypes.length > 0
    ? validResources.filter((resource) => {
        const resourceType = resource.resourceType;
        if (allowedResourceTypes.includes(resourceType)) {
          return true;
        }
        if (allowedResourceTypes.includes('COMMENT_BANK') && (resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS')) {
          return true;
        }
        if (allowedResourceTypes.includes('MARKING_TEMPLATE') && resourceType === 'MARKING_TEMPLATE') {
          return true;
        }
        return false;
      })
    : validResources;

  const availableSubjectsMap = new Map();
  filteredByResourceType.forEach((resource) => {
    (resource.subjects || []).forEach((s) => {
      if (s.code) {
        availableSubjectsMap.set(s.code, s.name || subjectCodeToTitle[s.code] || s.code);
      }
    });
  });
  const availableSubjectsList = Array.from(availableSubjectsMap.entries()).map(([code, title]) => ({ code, title })).sort((a, b) => a.title.localeCompare(b.title));
  // Detect duplicate subject titles — show "Name (Code)" only for duplicates
  const subjectTitleCounts = new Map();
  availableSubjectsList.forEach(({ title }) => subjectTitleCounts.set(title, (subjectTitleCounts.get(title) || 0) + 1));
  const availableSubjects = availableSubjectsList.map((s) => ({
    ...s,
    displayLabel: subjectTitleCounts.get(s.title) > 1 && s.code !== s.title ? `${s.title} (${s.code})` : s.title,
  }));
  const getSubjectDisplayLabel = (code) => {
    const entry = availableSubjects.find((s) => s.code === code);
    return entry ? entry.displayLabel : (subjectCodeToTitle[code] ?? code);
  };

  const filteredBySubject =
    selectedSubject === 'All Subjects'
      ? filteredByResourceType
      : filteredByResourceType.filter((resource) => (resource.subjects || []).some((s) => s.code === selectedSubject));

  const sharedBySet = new Set();
  filteredBySubject.forEach((resource) => {
    if (resource.sharedBy) {
      sharedBySet.add(resource.sharedBy);
    }
  });

  const filteredBySharedBy = selectedSharedBy === 'Shared by'
    ? filteredBySubject
    : filteredBySubject.filter((resource) => resource.sharedBy === selectedSharedBy);

  const sortedResources = [...filteredBySharedBy].sort((a, b) => {
    return sortOrder === 'newestFirst' ? b.sharedOnDate - a.sharedOnDate : a.sharedOnDate - b.sharedOnDate;
  });

  const uniqueSharedBy = Array.from(sharedBySet).sort();
  
  const hasMarkingToolTypes = sortedResources.some(resource => {
    const resourceType = resource.resourceType;
    return resourceType === 'MARKING_TEMPLATE' || resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS' || resourceType === 'SUCCESS_CRITERIA' || resourceType === 'SHORT_ANSWERS';
  });
  const shouldShowTypeColumn = showTypeColumnProp === false 
    ? false 
    : showTypeColumnProp === true 
    ? true 
    : (resourceCategory !== null || hasMarkingToolTypes);

  return (
    <MainContainer>
      <ImprovedSecondarySideBar isShowMenu={isShowMenu} setShowMenu={setShowMenu} />
      <ContentContainer>
        {tabletView && (
          <HeaderContainer>
            <MenuButton setShowMenu={setShowMenu} />
          </HeaderContainer>
        )}
        <FilterSortContainer>
          <FilterContainer>
            <FilterLabel>
              <img src={FilterSquare} alt="Filter" />
              Filters:
            </FilterLabel>
            <FilterDropdownWrap data-testid="filter-shared-by">
              <DropdownMenu
                primaryText={selectedSharedBy}
                noDefaultSelected={false}
                menuItems={[
                  { id: 'Shared by', title: 'Shared by' },
                  ...uniqueSharedBy.map((name) => ({ id: name, title: name })),
                ]}
                selectedIndex={selectedSharedBy === 'Shared by' ? 0 : uniqueSharedBy.indexOf(selectedSharedBy) + 1}
                onItemSelected={(item) => setSelectedSharedBy(item.id)}
              />
            </FilterDropdownWrap>
            <FilterDropdownWrap>
              <DropdownMenu
                primaryText={selectedSubject === 'All Subjects' ? selectedSubject : getSubjectDisplayLabel(selectedSubject)}
                noDefaultSelected={false}
                menuItems={[
                  { id: 'All Subjects', title: 'All Subjects' },
                  ...availableSubjects.map((s) => ({ id: s.code, title: s.displayLabel })),
                ]}
                selectedIndex={(() => {
                  if (selectedSubject === 'All Subjects') return 0;
                  const idx = availableSubjects.findIndex((s) => s.code === selectedSubject);
                  return idx >= 0 ? 1 + idx : 0;
                })()}
                onItemSelected={(item) => setSelectedSubject(item.id)}
              />
            </FilterDropdownWrap>
          </FilterContainer>
          <VerticalDivider />
          <SortContainer>
            <SortLabel>
              <img src={SortSquare} alt="Sort" />
              Sort by:
            </SortLabel>
            <SortButton
              active={sortOrder === 'newestFirst'}
              onClick={() => setSortOrder('newestFirst')}
            >
              New to Old
            </SortButton>
            <SortButton
              active={sortOrder === 'oldestFirst'}
              onClick={() => setSortOrder('oldestFirst')}
            >
              Old to New
            </SortButton>
          </SortContainer>
        </FilterSortContainer>
        <TableContainer>
          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>{resourceNameColumnLabel}</TableHeaderCell>
                <TableHeaderCell>Subjects</TableHeaderCell>
                {shouldShowTypeColumn && <TableHeaderCell>Type</TableHeaderCell>}
                <TableHeaderCell>Shared by</TableHeaderCell>
                <TableHeaderCell>
                  <HeaderCellContent>
                    Shared on <img src={ShareFilterIcon} alt="Filter" />
                  </HeaderCellContent>
                </TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {sortedResources.map((sharedResource) => {
                const resourceType = sharedResource.resourceType;
                const isMarkingTool = resourceType === 'MARKING_TEMPLATE' || resourceType === 'COMMENT_BANK' || resourceType === 'COMMENT_BANKS' || resourceType === 'SUCCESS_CRITERIA' || resourceType === 'SHORT_ANSWERS';
                const showTypeForThisRow = shouldShowTypeColumn && isMarkingTool;
                
                const sharedOnValue = sharedResource.sharedOn || 'N/A';
                
                return (
                  <TableRow key={sharedResource.id}>
                    <TemplateName>{sharedResource.itemTitle}</TemplateName>
                    <Subject>{sharedResource.subjects && sharedResource.subjects.length > 0 ? sharedResource.subjects.map((s) => getSubjectDisplayLabel(s.code)).join(', ') : '—'}</Subject>
                    {shouldShowTypeColumn && (
                      <TemplateType>
                        {showTypeForThisRow ? formatResourceType(sharedResource.resourceType) : ''}
                      </TemplateType>
                    )}
                    <SharedBy>{sharedResource.sharedBy}</SharedBy>
                    <SharedOn>{sharedOnValue}</SharedOn>
                  <ActionButtons>
                    <PreviewButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handlePreviewClick(sharedResource);
                      }}
                      disabled={isLoadingResourcePreview || acceptShareRequest.isPending || rejectShareRequest.isPending}
                    >
                      <img src={previewIcon} alt="Preview" />
                      Preview
                    </PreviewButton>
                    <AcceptButton
                      onClick={() => handleAccept(sharedResource)}
                      disabled={acceptShareRequest.isPending || rejectShareRequest.isPending}
                    >
                      <img src={acceptIcon} alt="Accept" />
                      Accept
                    </AcceptButton>
                    <RejectButton
                      onClick={() => handleRejectClick(sharedResource)}
                      disabled={acceptShareRequest.isPending || rejectShareRequest.isPending}
                    >
                      <img src={rejectIcon} alt="Reject" />
                      Reject
                    </RejectButton>
                  </ActionButtons>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </ContentContainer>
      <ActionPopup
        open={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          setSelectedResource(null);
        }}
        type="success"
        message={typeof successConfig.message === 'function' ? successConfig.message(selectedResource) : successConfig.message}
        primaryButtonLabel={typeof successConfig.buttonLabel === 'function' ? successConfig.buttonLabel(selectedResource) : successConfig.buttonLabel}
        primaryButtonAction={() => {
          setShowSuccessPopup(false);
          setSelectedResource(null);
          if (onCloseNotification) {
            onCloseNotification();
          }
          if (window.closeNotificationBar) {
            window.closeNotificationBar();
          }
          const path = typeof successConfig.redirectPath === 'function' ? successConfig.redirectPath(selectedResource) : successConfig.redirectPath;
          if (path) {
            window.location.href = path;
          }
        }}
        showCheckbox={true}
        onCheckboxChange={handleCheckboxChange}
      />
      <ActionPopup
        open={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setSelectedResource(null);
        }}
        type="reject"
        title={rejectConfig.title}
        message={rejectConfig.message}
        primaryButtonLabel="Reject"
        primaryButtonAction={handleConfirmReject}
        secondaryButtonLabel="Keep"
        secondaryButtonAction={() => {
          setShowRejectPopup(false);
          setSelectedResource(null);
        }}
        showDottedBorder={true}
      />
      {isPreviewDialogOpen && resourcePreviewData && (
        <>
          {resourcePreviewData.smartComments !== undefined ? (
            <CommentBankDialog
              setCommentBankPreviewDialog={(value) => {
                setIsPreviewDialogOpen(value);
                if (!value) setResourcePreviewData(null);
              }}
              commentBank={resourcePreviewData}
              showEditButton={false}
            />
          ) : resourcePreviewData.questions !== undefined || (resourcePreviewData.title && resourcePreviewData.classIds !== undefined) ? (
            <TaskPreviewDialog
              setTaskPreviewDialog={(value) => {
                setIsPreviewDialogOpen(value);
                if (!value) {
                  setResourcePreviewData(null);
                  setPreviewedSharedResource(null);
                }
              }}
              task={resourcePreviewData}
              shareId={previewedSharedResource?.id}
              resourceType={previewedSharedResource?.resourceType}
              resourceCategory={resourceCategory}
              onAcceptSuccess={onAcceptSuccess}
              successConfig={successConfig}
              onClose={() => {
                setIsPreviewDialogOpen(false);
                setResourcePreviewData(null);
                setPreviewedSharedResource(null);
              }}
            />
          ) : (previewedSharedResource?.resourceType === 'SHORT_ANSWERS' || (resourcePreviewData.highestMarks !== undefined && resourcePreviewData.criterias)) ? (
            <ShortAnswerDialog
              shortAnswer={resourcePreviewData}
              setShortAnswerPreviewDialog={(value) => {
                setIsPreviewDialogOpen(value);
                if (!value) {
                  setResourcePreviewData(null);
                  setPreviewedSharedResource(null);
                }
              }}
              showActionButton={false}
            />
          ) : (previewedSharedResource?.resourceType === 'SUCCESS_CRITERIA' || (resourcePreviewData.criterias && !resourcePreviewData.type && !resourcePreviewData.highestMarks)) ? (
            <SuccessCriteriaDialog
              successCriteria={resourcePreviewData}
              setSuccessCriteriaPreviewDialog={(value) => {
                setIsPreviewDialogOpen(value);
                if (!value) {
                  setResourcePreviewData(null);
                  setPreviewedSharedResource(null);
                }
              }}
              showActionButton={false}
              showEditButton={false}
            />
          ) : (
            <PreviewDialog
              setMarkingCriteriaPreviewDialog={(value) => {
                setIsPreviewDialogOpen(value);
                if (!value) setResourcePreviewData(null);
              }}
              markingCriterias={resourcePreviewData}
              showEditButton={false}
            />
          )}
        </>
      )}
    </MainContainer>
  );
};

export { SharedResourcesList };
